/**
 * SmartDownload — the QR/short-link target: cosmovexapps.com/get/<appId>
 *
 * Android → Google Play. iOS → App Store (if available). Wrong platform / no
 * store for it / desktop → a mesmerising showcase that converts: screenshots,
 * a "scan on your phone" QR, positive-only proof (strong ratings only), a trust
 * line, feature highlights and a "more from Cosmovex" row. SEO: MobileApplication
 * JSON-LD + canonical to the prerendered /apps/<id> landing (no duplicate content).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import QRCode from "qrcode";
import { apps } from "../data/apps";
import { ratingLabel } from "../data/appStats";
import shotsMap from "../data/screenshots.json";

const SITE = "https://cosmovexapps.com";

function detectOS() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  const isIOS = /iPhone|iPad|iPod/i.test(ua) ||
    (typeof navigator !== "undefined" && navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/i.test(ua);
  return { isIOS, isAndroid };
}

export default function SmartDownload() {
  const { appId } = useParams();
  const app = useMemo(() => apps.find((a) => a.id === appId), [appId]);
  const [redirecting, setRedirecting] = useState(false);
  const [qr, setQr] = useState("");

  const accent = "#A8720E"; // warm-paper light theme: flat amber accent for all apps
  const { isIOS, isAndroid } = detectOS();

  useEffect(() => {
    if (!app) return;
    let target = null;
    if (isIOS && app.appstore) target = app.appstore;
    else if (isAndroid && app.playstore) target = app.playstore;
    if (target) {
      setRedirecting(true);
      const t = setTimeout(() => window.location.replace(target), 650);
      return () => clearTimeout(t);
    }
  }, [app, isIOS, isAndroid]);

  useEffect(() => {
    if (!app) return;
    QRCode.toDataURL(`${SITE}/get/${app.id}`, { margin: 1, width: 320, color: { dark: "#26313B", light: "#ffffff" } })
      .then(setQr).catch(() => {});
  }, [app]);

  if (!app) {
    return (
      <Shell accent="#A8720E">
        <div className="sd-card">
          <div className="sd-name">App not found</div>
          <p className="sd-tag">Explore the full Cosmovex collection instead.</p>
          <a className="sd-btn" href="/apps">Browse all apps →</a>
        </div>
      </Shell>
    );
  }

  // Ratings/installs are deliberately NOT shown — low ratings or modest install
  // counts read as negative. Kept only for SEO schema, gated very strict so only
  // genuinely strong numbers ever surface in search rich results.
  const rating = ratingLabel(app.id);
  const stores = [];
  if (app.playstore) stores.push({ k: "play", label: "Get it on Google Play", url: app.playstore });
  if (app.appstore) stores.push({ k: "ios", label: "Download on the App Store", url: app.appstore });
  const wrongPlatform = (isIOS && !app.appstore) || (isAndroid && !app.playstore);
  const shots = (app.screenshots && app.screenshots.length) ? app.screenshots : (shotsMap[app.id] || []);
  const canonical = `${SITE}/apps/${app.id}`;
  const more = apps.filter((a) => a.id !== app.id && a.playstore)
    .sort((a) => (a.category === app.category ? -1 : 1)).slice(0, 8);

  const ld = {
    "@context": "https://schema.org", "@type": "MobileApplication",
    name: app.name, applicationCategory: "UtilitiesApplication",
    operatingSystem: app.appstore && app.playstore ? "Android, iOS" : app.appstore ? "iOS" : "Android",
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    url: canonical, image: `${SITE}${app.icon}`,
    installUrl: app.playstore || app.appstore,
    ...(shots.length && { screenshot: shots.map((s) => `${SITE}${s}`) }),
    ...(rating && rating.rating >= 4.5 && rating.count >= 30 && {
      aggregateRating: { "@type": "AggregateRating", ratingValue: rating.rating, ratingCount: rating.count },
    }),
  };

  return (
    <Shell accent={accent}>
      <Helmet>
        <title>{`${app.name} — ${app.tagline || "by Cosmovex"} | Download Free`}</title>
        <meta name="description" content={app.tagline || app.description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={app.name} />
        <meta property="og:description" content={app.tagline || app.description} />
        <meta property="og:image" content={`${SITE}${app.icon}`} />
        <script type="application/ld+json">{JSON.stringify(ld)}</script>
      </Helmet>

      {redirecting && (
        <div className="sd-redir"><div className="sd-spin" /><div>Opening {isIOS ? "the App Store" : "Google Play"}…</div></div>
      )}

      <div className="sd-card">
        <div className="sd-iconwrap">
          <img className="sd-icon" src={app.icon} alt={app.name} onError={(e) => { e.currentTarget.style.display = "none"; }} />
        </div>
        {app.category && <div className="sd-kick">{app.category}</div>}
        <h1 className="sd-name">{app.name}</h1>
        <p className="sd-tag">{app.tagline || app.description}</p>

        {wrongPlatform && (
          <div className="sd-note">{isIOS ? "Not on iOS yet — it's live on Android:" : "Not on Android yet — it's live on iOS:"}</div>
        )}

        <div className="sd-stores">
          {stores.map((s) => (
            <a key={s.k} className="sd-btn" href={s.url} target="_blank" rel="noopener noreferrer">
              {s.label} <span className="sd-arrow">→</span>
            </a>
          ))}
        </div>

        {shots.length > 0 && (
          <div className="sd-shots">
            {shots.map((s, i) => <img key={i} className="sd-shot" src={s} alt={`${app.name} screenshot ${i + 1}`} loading="lazy" />)}
          </div>
        )}

        {qr && (
          <div className="sd-qrcard">
            <img className="sd-qr" src={qr} alt="Scan to install" />
            <div className="sd-qrtxt"><b>Scan to get it on your phone</b><span>opens the right store automatically</span></div>
          </div>
        )}

        <div className="sd-trust">
          <span>● Free to download</span><span>● No account needed</span><span>● On-device</span>
        </div>

        {Array.isArray(app.features) && app.features.length > 0 && (
          <div className="sd-feats">{app.features.slice(0, 6).map((f) => <span key={f} className="sd-feat">{f}</span>)}</div>
        )}

        <div className="sd-avail">
          <span className={`sd-pill ${app.playstore ? "on" : ""}`}>{app.playstore ? "✓ Android" : "Android soon"}</span>
          <span className={`sd-pill ${app.appstore ? "on" : ""}`}>{app.appstore ? "✓ iOS" : "iOS soon"}</span>
        </div>

        {more.length > 0 && (
          <div className="sd-more">
            <div className="sd-more-h">More from Cosmovex</div>
            <Dock items={more} />
          </div>
        )}

        <div className="sd-foot">by <b>Cosmovex</b> · <a href="/apps">see all apps</a></div>
      </div>
    </Shell>
  );
}

function Shell({ accent, children }) {
  return (
    <div className="sd-root" style={{ "--a": accent }}>
      <style>{CSS}</style>
      <div className="sd-orb sd-orb1" />
      <div className="sd-orb sd-orb2" />
      {children}
    </div>
  );
}

/** macOS-dock magnification: icons swell + lift as the cursor glides across. */
function Dock({ items }) {
  const ref = useRef(null);
  const raf = useRef(0);

  const magnify = (clientX) => {
    cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(() => {
      const root = ref.current;
      if (!root) return;
      const icons = root.querySelectorAll(".sd-dicon");
      const RANGE = 130, MAX = 1.85;
      icons.forEach((ic) => {
        const r = ic.getBoundingClientRect();
        const center = r.left + r.width / 2;
        const d = Math.abs(clientX - center);
        const s = d < RANGE ? 1 + (MAX - 1) * Math.pow(1 - d / RANGE, 1.6) : 1;
        ic.style.transform = `scale(${s.toFixed(3)})`;
        ic.style.zIndex = String(Math.round(s * 10));
      });
    });
  };
  const reset = () => {
    cancelAnimationFrame(raf.current);
    ref.current?.querySelectorAll(".sd-dicon").forEach((ic) => { ic.style.transform = ""; ic.style.zIndex = ""; });
  };

  return (
    <div className="sd-dock" ref={ref}
         onMouseMove={(e) => magnify(e.clientX)} onMouseLeave={reset}
         onTouchMove={(e) => e.touches[0] && magnify(e.touches[0].clientX)} onTouchEnd={reset}>
      {items.map((a) => (
        <a key={a.id} className="sd-ditem" href={`/get/${a.id}`} title={a.name}>
          <span className="sd-dlabel">{a.name.split(/[:\-—]/)[0].trim()}</span>
          <img className="sd-dicon" src={a.icon} alt={a.name} loading="lazy"
               onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
        </a>
      ))}
    </div>
  );
}

const CSS = `
.sd-root{--bg:#FBFAF6;position:fixed;inset:0;overflow-y:auto;overflow-x:hidden;scrollbar-width:none;-ms-overflow-style:none;display:flex;align-items:flex-start;justify-content:center;
  padding:40px 22px;background:var(--bg);
  font-family:Inter,-apple-system,BlinkMacSystemFont,'Segoe UI',system-ui,sans-serif;color:#26313B}
.sd-orb{display:none}
.sd-orb1{width:420px;height:420px;top:-120px;left:-80px}
.sd-orb2{width:360px;height:360px;bottom:-120px;right:-60px}
.sd-card{position:relative;z-index:2;width:100%;max-width:460px;text-align:center;padding:40px 30px;border-radius:28px;
  background:#FFFFFF;
  border:1px solid #E1DCCE;box-shadow:0 1px 2px rgba(52,44,26,.06), 0 4px 14px rgba(52,44,26,.05);animation:sd-in .6s cubic-bezier(.2,.8,.2,1)}
@keyframes sd-in{from{opacity:0;transform:translateY(18px) scale(.98)}to{opacity:1;transform:none}}
.sd-iconwrap{width:104px;height:104px;margin:0 auto 18px;border-radius:24px;display:flex;align-items:center;justify-content:center;
  background:#F1EEE5;
  box-shadow:0 0 0 1px #E1DCCE, 0 18px 50px rgba(52,44,26,.14)}
.sd-icon{width:96px;height:96px;border-radius:22px;object-fit:cover}
.sd-kick{font-size:11px;font-weight:700;letter-spacing:.22em;text-transform:uppercase;color:var(--a);margin-bottom:8px}
.sd-name{font-size:29px;font-weight:800;margin:0 0 8px;line-height:1.12;color:#26313B}
.sd-tag{font-size:15px;color:#5B6B7C;margin:0 0 16px;line-height:1.5}
.sd-proof{display:flex;gap:10px;justify-content:center;align-items:center;margin-bottom:18px}
.sd-star{font-size:13px;font-weight:800;color:#A8720E;background:rgba(168,114,14,.1);border:1px solid rgba(168,114,14,.3);padding:5px 11px;border-radius:9px}
.sd-inst{font-size:13px;font-weight:700;color:#2E7D4F;background:rgba(46,125,79,.1);border:1px solid rgba(46,125,79,.3);padding:5px 11px;border-radius:9px}
.sd-note{font-size:13px;color:#A8720E;background:rgba(168,114,14,.08);border:1px solid rgba(168,114,14,.3);border-radius:10px;padding:9px 12px;margin-bottom:14px}
.sd-stores{display:flex;flex-direction:column;gap:10px;margin-bottom:18px}
.sd-btn{display:flex;align-items:center;justify-content:center;gap:8px;text-decoration:none;font-weight:700;font-size:15px;color:#221A06;background:#E8B44C;border:1px solid #D9A32E;border-radius:14px;padding:14px 18px;transition:transform .15s, box-shadow .15s;box-shadow:0 1px 2px rgba(52,44,26,.06), 0 4px 14px rgba(52,44,26,.05)}
.sd-btn:hover{transform:translateY(-2px);box-shadow:0 8px 26px rgba(52,44,26,.16)}
.sd-arrow{transition:transform .15s}.sd-btn:hover .sd-arrow{transform:translateX(3px)}
.sd-shots{display:flex;gap:12px;overflow-x:auto;padding:4px 2px 12px;margin-bottom:14px;scroll-snap-type:x mandatory;scrollbar-width:none;-ms-overflow-style:none}
.sd-shots::-webkit-scrollbar{display:none}
.sd-root::-webkit-scrollbar,.sd-root ::-webkit-scrollbar{width:0;height:0;display:none}
.sd-shot{height:300px;border-radius:18px;border:1px solid #E1DCCE;scroll-snap-align:center;flex:0 0 auto;box-shadow:0 14px 40px rgba(52,44,26,.14)}
.sd-qrcard{display:flex;align-items:center;gap:14px;text-align:left;background:#F1EEE5;border:1px solid #E1DCCE;border-radius:16px;padding:12px;margin-bottom:16px}
.sd-qr{width:84px;height:84px;border-radius:10px;background:#fff;padding:5px;border:1px solid #E1DCCE}
.sd-qrtxt{display:flex;flex-direction:column;gap:3px}.sd-qrtxt b{font-size:14px;color:#26313B}.sd-qrtxt span{font-size:12px;color:#5B6B7C}
.sd-trust{display:flex;flex-wrap:wrap;gap:6px 16px;justify-content:center;margin-bottom:16px;font-size:12px;color:#5B6B7C}
.sd-trust span{color:var(--a)} .sd-trust span::first-letter{color:var(--a)}
.sd-feats{display:flex;flex-wrap:wrap;gap:7px;justify-content:center;margin-bottom:18px}
.sd-feat{font-size:11.5px;font-weight:600;color:#3D4B59;background:#F1EEE5;border:1px solid #E1DCCE;border-radius:8px;padding:5px 10px}
.sd-avail{display:flex;gap:8px;justify-content:center;margin-bottom:20px}
.sd-pill{font-size:11px;font-weight:700;padding:5px 11px;border-radius:8px;color:#8A93A2;background:#F1EEE5;border:1px solid #E1DCCE}
.sd-pill.on{color:#2E7D4F;background:rgba(46,125,79,.1);border-color:rgba(46,125,79,.3)}
.sd-more{border-top:1px solid #E1DCCE;padding-top:18px;margin-bottom:14px}
.sd-more-h{font-size:11px;font-weight:700;letter-spacing:.18em;text-transform:uppercase;color:#8A93A2;margin-bottom:12px}
.sd-dock{display:flex;justify-content:center;align-items:flex-end;gap:9px;height:74px;padding:0 6px 2px;overflow:visible}
.sd-ditem{position:relative;flex:0 0 auto;text-decoration:none;display:flex;align-items:flex-end}
.sd-dicon{width:46px;height:46px;border-radius:13px;border:1px solid #E1DCCE;transform-origin:bottom center;transition:transform .16s cubic-bezier(.2,.9,.3,1.35);will-change:transform;display:block}
.sd-dlabel{position:absolute;bottom:100%;left:50%;transform:translateX(-50%);margin-bottom:9px;background:#26313B;color:#FBFAF6;font-size:11px;font-weight:600;padding:4px 9px;border-radius:8px;white-space:nowrap;opacity:0;pointer-events:none;transition:opacity .15s;border:1px solid #26313B;box-shadow:0 6px 18px rgba(52,44,26,.25)}
.sd-ditem:hover .sd-dlabel{opacity:1}
.sd-foot{font-size:12px;color:#8A93A2}.sd-foot a{color:#0F6BBF;text-decoration:none}
.sd-redir{position:fixed;inset:0;z-index:5;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:14px;background:rgba(251,250,246,.9);backdrop-filter:blur(6px);font-size:15px;color:#26313B}
.sd-spin{width:38px;height:38px;border-radius:50%;border:3px solid rgba(38,49,59,.18);border-top-color:var(--a);animation:sd-rot .8s linear infinite}
@keyframes sd-rot{to{transform:rotate(360deg)}}
@media(max-width:560px){
  .sd-card{padding:30px 18px;border-radius:22px}
  .sd-name{font-size:25px}
  .sd-dock{justify-content:flex-start;overflow-x:auto;overflow-y:hidden;scrollbar-width:none}
  .sd-dock::-webkit-scrollbar{display:none}
  .sd-shot{height:240px}
}
`;
